import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { platformMeta } from '../platformMeta';
import type { CalendarPost, CalendarSong } from '../types';
import { calendarStateOf } from './calendarFilterModel';
import { calendarMessages } from './calendarMessages';
import { postTitle } from './calendarPostView';
import { postCardLabel, postTime } from './postCardLabel';
import { PostStateMark } from './PostStateMark';
import { PostThumb } from './PostThumb';
import { useDisplayPostStatus } from '../displayPostStatus';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AgendaRowProps {
  post: CalendarPost;
  song: CalendarSong | undefined;
  /** The Needs you group names the day too. */
  withDate?: boolean;
  /** The row of the open post in the day panel. */
  isSelected?: boolean;
  onOpen: (post: CalendarPost) => void;
}

/** One post as a row (the List view and the day panel): picture, title, time and channel, mark. */
const AgendaRowComponent: React.FC<AgendaRowProps> = ({ post, song, withDate = false, isSelected, onOpen }) => {
  const intl = useIntl();
  const open = useCallback(() => onOpen(post), [onOpen, post]);
  // A scheduled post whose time has passed is going out: it shows as publishing.
  const shownStatus = useDisplayPostStatus(post);
  const format = intl.formatMessage(post.format === 'story' ? calendarMessages.story : calendarMessages.post);
  const when = withDate
    ? `${intl.formatDate(new Date(post.scheduledAt), { weekday: 'short', day: 'numeric', month: 'short' })}, ${postTime(
        intl,
        post
      )}`
    : postTime(intl, post);

  return (
    <button
      type="button"
      className={clsx(
        styles.row,
        isSelected === true && styles.rowOn,
        calendarStateOf(post.status) === 'needsYou' && styles.rowManual,
        calendarStateOf(post.status) === 'failed' && styles.rowFailed
      )}
      aria-label={postCardLabel(intl, { ...post, status: shownStatus }, song)}
      aria-current={isSelected === true ? 'true' : undefined}
      data-testid={`calendar-row-${post.uuid}`}
      onClick={open}
    >
      <PostThumb post={post} song={song} size="md" />
      <span className={styles.rowText}>
        <b>{`${postTitle(post, song)} · ${format}`}</b>
        <small>{`${when} · ${platformMeta[post.platform].name}`}</small>
      </span>
      <PostStateMark status={shownStatus} />
    </button>
  );
};

export const AgendaRow = TypedMemo(AgendaRowComponent);
